import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store';
import { patchScorecard, selectListData } from 'store/scorecards';
import {
  formatScorecardData,
  parseScorecardData,
  getScorecardStates,
  isOwnerScorecardEditingStatus,
  SelfAssessmentSetups,
} from 'helpers';
import { FormValues } from 'components/ScorecardDetails/interfaces';
import {
  FormGoal,
  FormSubGoal,
} from 'components/GoalsAndSubgoalsForm/interfaces';
import { selectDefaultAssessmentScaleGroupId } from 'store/options';
import { useScorecardContext } from 'contexts';
import { useToast, usePagePermissions, useScorecardData } from 'hooks';
import useNewGoalDefaults from 'hooks/scorecards/useNewGoalDefaults';
import { GoalSettingsValues } from 'components/GoalSettingsDialog';
import {
  AssessmentScaleGroupAlias,
  assessmentScaleGroupIdMap,
} from 'store/interfaces/ScorecardExternalSubgoalRO';
import {
  MetricFormat,
  MetricPerformanceFactor,
  SCORECARD_COMMON_GOAL_TYPE_REGULAR,
  SCORECARD_GOAL_TYPE_METRIC,
} from 'constants/scorecards';

export interface NewSubgoalValues {
  name: string;
  weight: number;
  description: string;
  assessmentScaleGroupId?: number;
  metricTarget?: string | number;
  metricFormat?: string;
  metricPerformanceFactor?: string;
}

export interface DeletedSubgoal {
  subgoal: FormSubGoal;
  index: number;
}

export interface DeletedGoal {
  goal: FormGoal;
  index: number;
}

export const convertGoalType = (
  goal: FormGoal,
  type: FormGoal['type'],
): FormGoal | undefined => {
  if (goal.type === type) return undefined;

  if (type === SCORECARD_COMMON_GOAL_TYPE_REGULAR) {
    return {
      ...goal,
      type,
      selfAssessmentSetup: SelfAssessmentSetups.request,
      allowScorecardOwnerEdits: true,
    };
  }

  if (type !== SCORECARD_GOAL_TYPE_METRIC) return { ...goal, type };

  return {
    ...goal,
    type,
    subgoals: goal.subgoals.map((subgoal) => ({
      ...subgoal,
      metricFormat: subgoal.metricFormat || MetricFormat.NUMBER,
      metricPerformanceFactor:
        subgoal.metricPerformanceFactor || MetricPerformanceFactor.PRORATED,
      metricTarget: subgoal.metricTarget ?? 0,
    })),
    selfAssessmentSetup: SelfAssessmentSetups.disable,
    allowScorecardMetricPmOwnerEdits: true,
    allowScorecardOwnerEditActualField: true,
  };
};

export const removeGoalFromGoals = (
  goals: FormGoal[],
  goalId: number,
): DeletedGoal | undefined => {
  const index = goals.findIndex((item) => item.id === goalId);
  if (index < 0) return undefined;

  const [goal] = goals.splice(index, 1);
  return { goal, index };
};

export const restoreGoalInGoals = (
  goals: FormGoal[],
  deletedGoal: DeletedGoal,
): void => {
  const restoredGoal = {
    ...deletedGoal.goal,
    subgoals: deletedGoal.goal.subgoals.map((subgoal) => {
      const restoredSubgoal = { ...subgoal };
      delete restoredSubgoal.id;
      return restoredSubgoal;
    }),
  };
  delete restoredGoal.id;
  goals.splice(deletedGoal.index, 0, restoredGoal);
};

export const removeSubgoalFromGoals = (
  goals: FormGoal[],
  goalId: number,
  subgoalId: number,
): DeletedSubgoal | undefined => {
  const goal = goals.find((item) => item.id === goalId);
  const index = goal?.subgoals.findIndex((item) => item.id === subgoalId);
  if (!goal || index === undefined || index < 0) return undefined;

  const [subgoal] = goal.subgoals.splice(index, 1);
  return { subgoal, index };
};

export const restoreSubgoalInGoals = (
  goals: FormGoal[],
  goalId: number,
  deletedSubgoal: DeletedSubgoal,
): boolean => {
  const goal = goals.find((item) => item.id === goalId);
  if (!goal) return false;

  const restoredSubgoal = { ...deletedSubgoal.subgoal };
  delete restoredSubgoal.id;
  goal.subgoals.splice(deletedSubgoal.index, 0, restoredSubgoal);
  return true;
};

const messages = defineMessages({
  goalsReordered: {
    id: 'pages.scorecards.view.goalsReordered',
    defaultMessage: 'Goals reordered',
  },
  subgoalsReordered: {
    id: 'pages.scorecards.view.subgoalsReordered',
    defaultMessage: 'Subgoals reordered',
  },
  subgoalAdded: {
    id: 'pages.scorecards.view.subgoalAdded',
    defaultMessage: 'Subgoal added',
  },
  goalAdded: {
    id: 'pages.scorecards.view.goalAdded',
    defaultMessage: 'Goal added',
  },
  goalSettingsSaved: {
    id: 'pages.scorecards.view.goalSettingsSaved',
    defaultMessage: 'Goal settings saved',
  },
  goalTypeSaved: {
    id: 'pages.scorecards.view.goalTypeSaved',
    defaultMessage: 'Goal type updated',
  },
  subgoalScaleSaved: {
    id: 'pages.scorecards.view.subgoalScaleSaved',
    defaultMessage: 'Assessment scale updated',
  },
  subgoalScaleSavedWeightZeroed: {
    id: 'pages.scorecards.view.subgoalScaleSavedWeightZeroed',
    defaultMessage:
      'Assessment scale updated. Text subgoals are not scored, so its weight was set to 0%.',
  },
});

/**
 * A text subgoal is never scored, so it cannot carry weight. Mirrors the edit
 * form's behaviour (`GoalsAndSubgoalsForm/SubGoal.component.tsx`), expressed as
 * a rule so it can be verified without the save plumbing around it.
 */
export const textScaleZeroesWeight = (
  assessmentScaleGroupId: number,
  currentWeight: number | string | null | undefined,
): boolean =>
  assessmentScaleGroupId ===
    assessmentScaleGroupIdMap[AssessmentScaleGroupAlias.TEXT] &&
  Number(currentWeight ?? 0) !== 0;

const arrayMove = <T>(items: T[], fromIdx: number, toIdx: number): T[] => {
  const next = items.slice();
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
};

export default function useScorecardStructureSave() {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const showMessage = useToast();
  const scorecard = useScorecardData();
  const { id, store, enqueueScorecardMutation } = useScorecardContext();
  const { isOwnScorecardsPage } = usePagePermissions();
  const defaultScaleGroupId = useSelector(selectDefaultAssessmentScaleGroupId);
  const newGoalDefaults = useNewGoalDefaults();

  const { version: listVersion } = useSelector(selectListData(store, id)) || {};
  const { version: userScorecardVersion } =
    useSelector(selectListData('userScorecards', id)) || {};
  const version = (scorecard.version ||
    listVersion ||
    userScorecardVersion) as number;
  const scorecardSnapshot = useMemo(
    () => ({ ...scorecard, version }),
    [scorecard, version],
  );

  const persist = useCallback(
    async (
      updateGoals: (goals: FormGoal[]) => boolean | void,
      message?: string | (() => string),
    ) => {
      const updatedScorecard = await enqueueScorecardMutation(
        scorecardSnapshot,
        async (latestScorecard) => {
          const goals = parseScorecardData(latestScorecard.goals);
          if (updateGoals(goals) === false) return undefined;
          const { isFeedbackCollected, isCompleted } = getScorecardStates(
            latestScorecard.status,
          );
          const withRestrictions =
            isOwnScorecardsPage || isFeedbackCollected || isCompleted;
          const isActiveOwnScorecard =
            isOwnScorecardsPage &&
            isOwnerScorecardEditingStatus(latestScorecard.status);

          const data = formatScorecardData(
            { id, goals, totalWeight: 0, entity: 'scorecard' } as FormValues,
            latestScorecard.version,
            withRestrictions,
            isActiveOwnScorecard,
          );

          return dispatch(
            patchScorecard({ id, store, data: { ...data, inlineEdit: true } }),
          ).unwrap();
        },
      );

      if (updatedScorecard && message) {
        showMessage({
          severity: 'success',
          message: typeof message === 'function' ? message() : message,
        });
      }

      return updatedScorecard;
    },
    [
      dispatch,
      enqueueScorecardMutation,
      id,
      store,
      isOwnScorecardsPage,
      scorecardSnapshot,
      showMessage,
    ],
  );

  const reorderGoals = useCallback(
    (fromIdx: number, toIdx: number) => {
      if (fromIdx === toIdx) return undefined;
      return persist((goals) => {
        const reorderedGoals = arrayMove(goals, fromIdx, toIdx);
        goals.splice(0, goals.length, ...reorderedGoals);
      }, formatMessage(messages.goalsReordered));
    },
    [persist, formatMessage],
  );

  const reorderSubgoals = useCallback(
    (goalId: number, fromIdx: number, toIdx: number) => {
      if (fromIdx === toIdx) return undefined;
      return persist((goals) => {
        const goal = goals.find((item) => item.id === goalId);
        if (!goal) return false;
        goal.subgoals = arrayMove(goal.subgoals, fromIdx, toIdx);
        return true;
      }, formatMessage(messages.subgoalsReordered));
    },
    [persist, formatMessage],
  );

  const createSubgoal = useCallback(
    (goalId: number, values: NewSubgoalValues) =>
      persist((goals) => {
        const goal = goals.find((item) => item.id === goalId);
        if (!goal) return false;

        const template = goal.subgoals[0];
        const newSubgoal: FormSubGoal = {
          name: values.name,
          description: values.description ?? '',
          weight: values.weight ?? 0,
          orderRank: goal.subgoals.length,
          assessmentScaleGroupId:
            values.assessmentScaleGroupId ??
            template?.assessmentScaleGroupId ??
            defaultScaleGroupId,
          metricFormat: values.metricFormat,
          metricTarget: values.metricTarget,
          metricPerformanceFactor: values.metricPerformanceFactor,
        };
        goal.subgoals = [...goal.subgoals, newSubgoal];
        return true;
      }, formatMessage(messages.subgoalAdded)),
    [persist, formatMessage, defaultScaleGroupId],
  );

  const deleteSubgoal = useCallback(
    async (
      goalId: number,
      subgoalId: number,
    ): Promise<DeletedSubgoal | undefined> => {
      let deletedSubgoal: DeletedSubgoal | undefined;
      await persist((goals) => {
        deletedSubgoal = removeSubgoalFromGoals(goals, goalId, subgoalId);
        return Boolean(deletedSubgoal);
      });
      return deletedSubgoal;
    },
    [persist],
  );

  const restoreSubgoal = useCallback(
    (goalId: number, deletedSubgoal: DeletedSubgoal) =>
      persist((goals) => restoreSubgoalInGoals(goals, goalId, deletedSubgoal)),
    [persist],
  );

  const createGoal = useCallback(
    () =>
      persist((goals) => {
        goals.push(newGoalDefaults(goals.length));
      }, formatMessage(messages.goalAdded)),
    [persist, formatMessage, newGoalDefaults],
  );

  const deleteGoal = useCallback(
    async (goalId: number): Promise<DeletedGoal | undefined> => {
      let deletedGoal: DeletedGoal | undefined;
      await persist((goals) => {
        deletedGoal = removeGoalFromGoals(goals, goalId);
        return Boolean(deletedGoal);
      });
      return deletedGoal;
    },
    [persist],
  );

  const restoreGoal = useCallback(
    (deletedGoal: DeletedGoal) =>
      persist((goals) => {
        restoreGoalInGoals(goals, deletedGoal);
      }),
    [persist],
  );

  const createCommonGoal = useCallback(
    (template: FormGoal) =>
      persist((goals) => {
        goals.push({ ...template, orderRank: goals.length });
      }, formatMessage(messages.goalAdded)),
    [persist, formatMessage],
  );

  const updateGoalSettings = useCallback(
    (goalId: number, values: GoalSettingsValues) =>
      persist((goals) => {
        const goal = goals.find((item) => item.id === goalId);
        if (!goal) return false;
        Object.assign(goal, values);
        return true;
      }, formatMessage(messages.goalSettingsSaved)),
    [persist, formatMessage],
  );

  const updateSubgoalScale = useCallback(
    (goalId: number, subgoalId: number, assessmentScaleGroupId: number) => {
      let zeroesWeight = false;
      return persist(
        (goals) => {
          const goal = goals.find((item) => item.id === goalId);
          const subgoal = goal?.subgoals.find((item) => item.id === subgoalId);
          if (!subgoal) return false;

          subgoal.assessmentScaleGroupId = assessmentScaleGroupId;
          zeroesWeight = textScaleZeroesWeight(
            assessmentScaleGroupId,
            subgoal.weight,
          );
          if (zeroesWeight) {
            subgoal.weight = 0;
          }
          return true;
        },
        () =>
          formatMessage(
            zeroesWeight
              ? messages.subgoalScaleSavedWeightZeroed
              : messages.subgoalScaleSaved,
          ),
      );
    },
    [persist, formatMessage],
  );

  const updateGoalType = useCallback(
    (goalId: number, type: FormGoal['type']) =>
      persist((goals) => {
        const goalIndex = goals.findIndex((goal) => goal.id === goalId);
        if (goalIndex < 0) return false;
        const convertedGoal = convertGoalType(goals[goalIndex], type);
        if (!convertedGoal) return false;
        goals.splice(goalIndex, 1, convertedGoal);
        return true;
      }, formatMessage(messages.goalTypeSaved)),
    [persist, formatMessage],
  );

  return {
    reorderGoals,
    reorderSubgoals,
    createSubgoal,
    deleteSubgoal,
    restoreSubgoal,
    createGoal,
    deleteGoal,
    restoreGoal,
    createCommonGoal,
    updateGoalSettings,
    updateSubgoalScale,
    updateGoalType,
  };
}
